<template>
	<article class="evolution_card" :class="{ 'is-fill': isTableShown }">
		<header class="evolution_head">
			<div class="evolution_title_wrap">
				<span class="evolution_title">{{ title }}</span>
			</div>
			<!-- Con un ítem elegido, la card alterna gráfico ↔ tabla (Steve,
				2026-10-07): la tabla es la misma evolución en filas. -->
			<button v-if="selectionLabel"
				type="button"
				class="view_toggle"
				:aria-pressed="String(showTable)"
				@click="showTable = !showTable">
				<component :is="showTable ? 'ChartLine' : 'DashboardSquare'" class="view_toggle_icon" />
				{{ showTable ? "Ver gráfico" : "Ver tabla" }}
			</button>
			<!-- Chips de métrica: selección única. Una métrica a la vez porque
				mezclar pesos con porcentajes en un mismo eje no se lee. Con una
				selección del header no hay chips: el gráfico muestra todas las
				métricas a la vez (barras de venta + líneas de margen, dos ejes). -->
			<div v-else class="metric_chips">
				<button v-for="metric in metrics" :key="metric.key"
					type="button"
					class="filter_btn"
					:class="{ 'is-active': metric.key === activeMetric }"
					:aria-pressed="String(metric.key === activeMetric)"
					@click="activeMetric = metric.key">
					{{ metric.label }}
				</button>
			</div>
		</header>

		<div class="evolution_canvas">
			<p v-if="!hasData" class="evolution_state">
				Sin datos para el período seleccionado.
			</p>
			<ProfitabilityEvolutionTable v-else-if="isTableShown"
				:buckets="monthLabels"
				:rows="tableRows" />
			<KpiComposedChart v-else
				:monthHeaders="monthLabels"
				:series="series" />
		</div>
	</article>
</template>

<script>
import KpiComposedChart from "@/components/financialReports/charts/KpiComposedChart.vue";
import ChartLine from "@/components/svg/ChartLine.vue";
import DashboardSquare from "@/components/svg/DashboardSquare.vue";
import { bucketToHeaderLabel } from "@/views/financialReports/utils/periodFormatting";
import { marginByMetricKey } from "@/utils/profitabilityMargins";
import ProfitabilityEvolutionTable from "./ProfitabilityEvolutionTable.vue";

// Card "Evolución de Ventas & Márgenes" del drawer de categorías, con dos
// modos sobre el MISMO payload (`evolution.metrics[].series[]`, por métrica
// una serie por ítem), sin backend propio:
//
// - Sin ítem elegido: una línea por ítem de la categoría (tienda/marca) sobre
//   los 12 meses, para la métrica elegida en los chips. Los tokens son los de
//   los progress bars del tema, así una tienda conserva su color entre las
//   barras de las cards y su línea acá.
// - Con una selección parcial en el select del header (una tienda o varias):
//   el mismo gráfico del tab pero de esa selección — barras de sus ventas
//   (pesos, eje izquierdo) y una línea por escalón de margen (%, eje derecho),
//   con la identidad por escalón de `utils/profitabilityMargins.js`. Los
//   valores son el `total` que el backend calcula por métrica sobre los ítems
//   del payload (ya acotado por el filtro del header): el agregado sale de
//   los montos, porque dos porcentajes por tienda no se pueden sumar.
//
// Los valores llegan listos del backend (pesos en Ventas, % entero en los
// márgenes; `null` donde no hubo ventas). Reusa `KpiComposedChart` (el del
// gráfico del tab): resuelve ejes, leyenda, tooltip por unidad y el color por
// serie vía `colorToken`.
//
// En el modo por ítem el botón "Ver tabla" (D5 de las observaciones,
// 2026-10-07) reemplaza el gráfico por `ProfitabilityEvolutionTable` con las
// mismas series en filas: Ventas Netas y, por escalón, el margen en pesos
// (`total_amount`, que manda el backend) y en % (`total`). La vista elegida
// es estado local de la card y se conserva al cambiar de ítem o de rango.
const TITLE = "Evolución de Ventas & Márgenes";
const SALES_METRIC = "ventas";
const SALES_NAME = "Ventas";
const SALES_TABLE_LABEL = "Ventas Netas";
// Las barras de venta del modo por selección van del mismo color que las del
// gráfico del tab (Steve, 2026-10-07: «respetar los colores de la base»);
// sin token tomaban el primer color de la rueda del chart.
const SALES_COLOR_TOKEN = "--report-chart-sales";

export default {
	name: "ProfitabilityCategoryEvolution",

	components: { ChartLine, DashboardSquare, KpiComposedChart, ProfitabilityEvolutionTable },

	props: {
		// Primer día de cada bucket en ISO, en orden cronológico (12 meses, 8
		// trimestres o hasta 5 años según `period`).
		months: { type: Array, default: () => [] },
		// Granularidad de los buckets: "month" | "quarter" | "year". Sólo
		// cambia las etiquetas del eje; los valores ya vienen por bucket.
		period: { type: String, default: "month" },
		// [{ key, label, unit: 'money'|'percent', series: [{name, values}],
		//    total: values }]
		metrics: { type: Array, default: () => [] },
		// Token de color por ítem (`--progress-bar-N`), el mismo de las barras
		// de las cards: lo asigna el body con `itemColorTokens`.
		itemColors: { type: Object, default: () => ({}) },
		// Etiqueta de la selección parcial del header ("Tienda 1", "3
		// tiendas"); null con todos los ítems tildados. Con etiqueta, la card
		// pasa al modo por selección (barras de venta + líneas de margen).
		selectionLabel: { type: String, default: null },
	},

	data() {
		return {
			// Métrica activa. La primera es Ventas (la del diseño al abrir).
			activeMetric: this.metrics.length ? this.metrics[0].key : null,
			// Modo por ítem: tabla en vez de gráfico.
			showTable: false,
		};
	},

	computed: {
		title() {
			return this.selectionLabel ? `${TITLE} ${this.selectionLabel}` : TITLE;
		},

		// Tabla a la vista: la card se estira hasta el alto disponible del
		// drawer (Steve, 2026-10-07) para mostrar todas las filas sin scroll;
		// sólo si aun así no entran, scrollea adentro de la tabla, nunca el
		// drawer. El gráfico conserva su alto fijo.
		isTableShown() {
			return Boolean(this.selectionLabel) && this.showTable;
		},

		monthLabels() {
			return this.months.map((iso) => bucketToHeaderLabel(iso, this.period));
		},

		current() {
			return this.metrics.find((metric) => metric.key === this.activeMetric)
				|| this.metrics[0]
				|| null;
		},

		hasData() {
			if (!this.months.length) return false;
			if (this.selectionLabel) return this.selectionSeries.length > 0;
			return Boolean(this.current && this.current.series.length);
		},

		// Filas de la tabla del modo por ítem: Ventas Netas y, por escalón en
		// el orden de la cascada, el margen en pesos y en % (la fila de %
		// lleva el tinte suave de su escalón). Un escalón sin `total_amount`
		// (payload viejo) muestra sólo su %.
		tableRows() {
			const rows = [];
			this.metrics.forEach((metric) => {
				if (!Array.isArray(metric.total)) return;
				if (metric.key === SALES_METRIC) {
					rows.push({
						key: metric.key, label: SALES_TABLE_LABEL, unit: "money",
						values: metric.total, strong: true,
					});
					return;
				}
				const margin = marginByMetricKey(metric.key);
				const label = margin ? margin.label : metric.label;
				if (Array.isArray(metric.total_amount)) {
					rows.push({
						key: `${metric.key}:amount`, label, unit: "money", values: metric.total_amount,
					});
				}
				rows.push({
					key: `${metric.key}:percent`,
					label: `${label} %`,
					unit: "percent",
					values: metric.total,
					colorToken: margin ? margin.colorToken : null,
					tinted: true,
				});
			});
			return rows;
		},

		series() {
			if (this.selectionLabel) return this.selectionSeries;
			if (!this.current) return [];
			return this.current.series.map((serie) => ({
				name: serie.name,
				type: "line",
				unit: this.current.unit,
				colorToken: this.itemColors[serie.name],
				data: serie.values,
			}));
		},

		// Modo por selección: [Ventas (bar, money), …márgenes (line, percent)]
		// con el `total` de cada métrica, en el orden de `metrics` (el de la
		// cascada). Una métrica sin `total` no se dibuja.
		selectionSeries() {
			const series = [];
			this.metrics.forEach((metric) => {
				if (!Array.isArray(metric.total)) return;
				if (metric.key === SALES_METRIC) {
					series.push({
						name: SALES_NAME,
						type: "bar",
						unit: "money",
						colorToken: SALES_COLOR_TOKEN,
						data: metric.total,
					});
					return;
				}
				const margin = marginByMetricKey(metric.key);
				series.push({
					name: margin ? margin.label : metric.label,
					type: "line",
					unit: metric.unit,
					colorToken: margin ? margin.colorToken : undefined,
					data: metric.total,
				});
			});
			return series;
		},
	},

	watch: {
		// Cambió la categoría (o el mes): si la métrica activa ya no existe
		// —Servicios no tiene Producto ni Contribución— volvemos a la primera.
		metrics(list) {
			if (!list.some((metric) => metric.key === this.activeMetric)) {
				this.activeMetric = list.length ? list[0].key : null;
			}
		},
	},
};
</script>

<style scoped lang="scss">
// Misma chrome que las cards del reporte (surface + borde + radio 16).
.evolution_card {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 16px;
	box-shadow: var(--report-shadow-card);
	padding: 12px 16px 16px;
	display: flex;
	flex-direction: column;
}

.evolution_head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 8px;
}

.evolution_title_wrap {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
}

.evolution_title {
	font-size: 16px;
	font-weight: 600;
	color: var(--report-text);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

// Mismo botón de acción que "Comparación / Tabla" de las tablas del reporte.
.view_toggle {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	flex-shrink: 0;
	padding: 6px 12px;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	font-size: 13px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	cursor: pointer;

	&:hover {
		background: var(--report-surface);
	}

	&:focus-visible {
		outline: 2px solid var(--report-accent-border);
		outline-offset: 2px;
	}
}

.view_toggle_icon {
	width: 16px;
	height: 16px;
}

.metric_chips {
	display: flex;
	align-items: center;
	gap: 4px;
	flex-shrink: 0;
	flex-wrap: wrap;
	justify-content: flex-end;
}

// Mismo pill que los sub-filtros del header del tab.
.filter_btn {
	background: transparent;
	border: 1px solid transparent;
	border-radius: 8px;
	padding: 4px 10px;
	font-size: 12px;
	font-weight: 500;
	color: var(--report-text-muted);
	cursor: pointer;
	transition: background-color 160ms ease, color 160ms ease;

	&:hover {
		background: var(--report-surface);
		color: var(--report-text);
	}

	&:focus-visible {
		outline: 2px solid var(--report-accent-border);
		outline-offset: 2px;
	}

	&.is-active {
		background: var(--report-accent-bg);
		border-color: var(--report-accent-border);
		color: var(--report-accent-text);
	}
}

.evolution_canvas {
	height: 320px;
	min-height: 0;
	display: flex;
	align-items: stretch;
	justify-content: stretch;
	position: relative;
}

// Con la tabla a la vista la card mide lo que miden sus filas y puede
// ENCOGER (nunca crecer de más) hasta el alto que le deje el drawer: la
// tabla scrollea adentro sólo cuando no entran todas las filas, y nunca
// deja blanco bajo la última (Steve, 2026-10-07: con `flex: 1` la card se
// estiraba al fondo del drawer y la barra horizontal quedaba lejos de las
// filas). El canvas suelta su alto fijo: el gráfico lo conserva.
.evolution_card.is-fill {
	flex: 0 1 auto;
	min-height: 0;

	.evolution_canvas {
		flex: 0 1 auto;
		height: auto;
		min-height: 0;
	}
}

.evolution_state {
	margin: auto;
	color: var(--report-text-muted);
	font-size: 13px;
	text-align: center;
}
</style>
